import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

type SessionFormData = {
  briefing_start_time?: string;
  briefing_duration?: string;       // hh:mm format
  session_start_time?: string;
  session_end_time?: string;
  sim_duration?: string;            // hh:mm format
  captain_pf_time?: string;         // hh:mm format
  fo_pf_time?: string;              // hh:mm format
  debrief_duration?: string;        // hh:mm format
};

const INPUT_CLASS =
  'w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate';
const CALC_CLASS =
  'w-full px-3 py-2 bg-gray-100 border border-steel/30 rounded-md text-navy/60 cursor-not-allowed';
const LABEL_CLASS = 'block text-sm font-medium text-navy mb-1';

/** Convert hh:mm to total minutes */
export function hhmmToMinutes(hhmm: string | undefined): number | null {
  if (!hhmm) return null;
  const parts = hhmm.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  return h * 60 + m;
}

/** Convert total minutes to hh:mm */
export function minutesToHhmm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Add hh:mm duration to an HH:MM time, return HH:MM */
function addDuration(time: string, duration: string | undefined, fallbackMinutes: number): string {
  if (!time) return '';
  const mins = hhmmToMinutes(duration) ?? fallbackMinutes;
  const parts = time.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const total = h * 60 + m + mins;
  const rh = Math.floor(total / 60) % 24;
  const rm = total % 60;
  return `${String(rh).padStart(2, '0')}:${String(rm).padStart(2, '0')}`;
}

export default function SessionTimingSection() {
  const { register, setValue, control } = useFormContext<SessionFormData>();

  const briefingStart = useWatch({ control, name: 'briefing_start_time' });
  const briefingDuration = useWatch({ control, name: 'briefing_duration' });
  const sessionStart = useWatch({ control, name: 'session_start_time' });
  const simDuration = useWatch({ control, name: 'sim_duration' });
  useWatch({ control, name: 'captain_pf_time' });

  const effectiveSimMinutes = hhmmToMinutes(simDuration) ?? 240;

  // Auto-calc end times
  const briefingEnd = addDuration(briefingStart ?? '', briefingDuration, 60);
  const sessionEnd = addDuration(sessionStart ?? '', simDuration, 240);

  useEffect(() => {
    if (sessionEnd) {
      setValue('session_end_time', sessionEnd, { shouldDirty: true });
    }
  }, [sessionEnd, setValue]);

  // Auto-calc: when captain PF changes, set FO PF = sim duration - captain PF
  const handleCaptainPfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const captainMins = hhmmToMinutes(e.target.value);
    if (captainMins !== null && captainMins >= 0 && captainMins <= effectiveSimMinutes) {
      setValue('fo_pf_time', minutesToHhmm(effectiveSimMinutes - captainMins), { shouldDirty: true });
    }
  };

  const handleFoPfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const foMins = hhmmToMinutes(e.target.value);
    if (foMins !== null && foMins >= 0 && foMins <= effectiveSimMinutes) {
      setValue('captain_pf_time', minutesToHhmm(effectiveSimMinutes - foMins), { shouldDirty: true });
    }
  };

  // Default PF values: 50/50 split of sim duration
  const halfSim = minutesToHhmm(Math.round(effectiveSimMinutes / 2));
  const otherHalfSim = minutesToHhmm(effectiveSimMinutes - Math.round(effectiveSimMinutes / 2));

  return (
    <div className="space-y-4">
      {/* Briefing Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="briefing_start_time" className={LABEL_CLASS}>Pre-Session Briefing Start Time</label>
          <input id="briefing_start_time" type="time" {...register('briefing_start_time')} className={INPUT_CLASS} />
        </div>
        <div>
          <label htmlFor="briefing_duration" className={LABEL_CLASS}>Briefing duration</label>
          <input id="briefing_duration" type="time" {...register('briefing_duration')} placeholder="01:00" className={INPUT_CLASS} />
        </div>
        <div>
          <label htmlFor="briefing_end_time_calculated" className={LABEL_CLASS}>Calculate Briefing End time</label>
          <input id="briefing_end_time_calculated" type="time" value={briefingEnd} disabled className={CALC_CLASS} />
        </div>
      </div>

      {/* Session Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="session_start_time" className={LABEL_CLASS}>Session Start Time</label>
          <input id="session_start_time" type="time" {...register('session_start_time')} className={INPUT_CLASS} />
        </div>
        <div>
          <label htmlFor="sim_duration" className={LABEL_CLASS}>Session duration</label>
          <input id="sim_duration" type="time" {...register('sim_duration')} placeholder="04:00" className={INPUT_CLASS} />
        </div>
        <div>
          <label htmlFor="session_end_time_calculated" className={LABEL_CLASS}>Calculate Session End time</label>
          <input id="session_end_time_calculated" type="time" value={sessionEnd} disabled className={CALC_CLASS} />
        </div>
      </div>

      {/* PF Time Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="captain_pf_time" className={LABEL_CLASS}>Captain Time as Pilot Flying (hh:mm)</label>
          <input
            id="captain_pf_time" type="time"
            {...register('captain_pf_time', { onChange: handleCaptainPfChange })}
            placeholder={halfSim}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="fo_pf_time" className={LABEL_CLASS}>F/O Time as Pilot Flying (hh:mm)</label>
          <input
            id="fo_pf_time" type="time"
            {...register('fo_pf_time', { onChange: handleFoPfChange })}
            placeholder={otherHalfSim}
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* Debriefing Duration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="debrief_duration" className={LABEL_CLASS}>Debriefing duration</label>
          <input id="debrief_duration" type="time" {...register('debrief_duration')} placeholder="01:00" className={INPUT_CLASS} />
        </div>
        <div className="flex items-end pb-2">
          <p className="text-sm text-steel font-medium">Briefing-Session-Debriefing are considered as a single time block</p>
        </div>
      </div>
    </div>
  );
}
